import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { type Event } from '../lib/api/events';
import { supabase, supabaseAdmin } from '../lib/supabase';
import { listCalendarEvents } from '../lib/google-calendar';
import { bookingNotificationService } from '@/services/bookingNotificationService';
import { getTimezoneFromCoordinates, convertLocalTimeToUTC, getIANATimezone, convertTimeBetweenTimezones } from '@/utils/timezoneUtils';

const bookingFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  salesRepName: z.string().min(1, 'Sales representative name is required'),
  selectedDate: z.string().min(1, 'Please select a date'),
  selectedTime: z.string().min(1, 'Please select a time'),
  // Additional fields
  levels: z.enum(['One', 'Two', 'Three'], {
    required_error: 'Please select number of levels',
  }),
  atticAccess: z.enum(['In Garage', 'In Bedroom', 'In Hallway'], {
    required_error: 'Please select attic access location',
  }),
  mountType: z.enum(['Roof Mount', 'Ground Mount', 'Both'], {
    required_error: 'Please select mount type',
  }),
  metersOnSite: z.enum(['One', 'Two', 'Three'], {
    required_error: 'Please select number of meters',
  }),
  electricalPanels: z.enum(['One', 'Two', 'Three'], {
    required_error: 'Please select number of electrical panels',
  }),
  animals: z.enum(['Dogs', 'Cats', 'None'], {
    required_error: 'Please select animals present',
  }),
  hasBattery: z.enum(['Yes', 'No'], {
    required_error: 'Please specify if the project has a battery',
  }),
  requiresConstruction: z.enum(['Yes', 'No'], {
    required_error: 'Please specify if construction is required',
  }),
  constructionDetails: z.string().optional(),
  installerNote: z.string().optional(),
  // Address fields from Google Places
  address: z.string().min(1, 'Address is required'),
  streetAddress: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
  country: z.string().min(1, 'Country is required'),
  latitude: z.number(),
  longitude: z.number(),
});

type BookingFormData = z.infer<typeof bookingFormSchema>;

interface BookingFormProps {
  event: Event;
  onSubmit?: (data: BookingFormData) => void;
  initialAddress?: {
    address: string;
    street_address: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  urlParams?: URLSearchParams;
  projectId?: string; // Added optional projectId
}

// US & Canada Timezone Mapping
const timeZoneMap: Record<string, string> = {
  // Pacific
  'America/Los_Angeles': 'Pacific',
  'America/Vancouver': 'Pacific',
  'America/Tijuana': 'Pacific',
  'America/Whitehorse': 'Pacific',
  'America/Dawson': 'Pacific',
  'America/Metlakatla': 'Pacific',
  // Mountain
  'America/Denver': 'Mountain',
  'America/Edmonton': 'Mountain',
  'America/Boise': 'Mountain',
  'America/Phoenix': 'Mountain',
  'America/Yellowknife': 'Mountain',
  'America/Cambridge_Bay': 'Mountain',
  'America/Inuvik': 'Mountain',
  // Central
  'America/Chicago': 'Central',
  'America/Winnipeg': 'Central',
  'America/Regina': 'Central',
  'America/Rankin_Inlet': 'Central',
  'America/Matamoros': 'Central',
  'America/Monterrey': 'Central',
  'America/Mexico_City': 'Central',
  'America/Belize': 'Central',
  'America/Managua': 'Central',
  'America/Guatemala': 'Central',
  // Eastern
  'America/New_York': 'Eastern',
  'America/Toronto': 'Eastern',
  'America/Nassau': 'Eastern',
  'America/Montreal': 'Eastern',
  'America/Detroit': 'Eastern',
  'America/Thunder_Bay': 'Eastern',
  'America/Iqaluit': 'Eastern',
  'America/Pangnirtung': 'Eastern',
  'America/Port-au-Prince': 'Eastern',
  // Atlantic
  'America/Halifax': 'Atlantic',
  'America/Glace_Bay': 'Atlantic',
  'America/Moncton': 'Atlantic',
  'America/Goose_Bay': 'Atlantic',
  'America/Barbados': 'Atlantic',
  'America/Blanc-Sablon': 'Atlantic',
  'America/Thule': 'Atlantic',
  // Newfoundland
  'America/St_Johns': 'Newfoundland',
  // Alaska
  'America/Anchorage': 'Alaska',
  'America/Juneau': 'Alaska',
  'America/Nome': 'Alaska',
  'America/Sitka': 'Alaska',
  'America/Yakutat': 'Alaska',
  // Hawaii-Aleutian
  'Pacific/Honolulu': 'Hawaii-Aleutian',
  'America/Adak': 'Hawaii-Aleutian',
};

export default function BookingForm({ event, onSubmit, initialAddress, urlParams, projectId }: BookingFormProps) {
  const navigate = useNavigate();
  
  // Helper function to format time slots nicely
  const formatTimeSlot = (timeStr: string) => {
    try {
      // Handle different time formats
      if (timeStr.includes('AM') || timeStr.includes('PM')) {
        return timeStr; // Already formatted
      }
      
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${displayHour}:${displayMinutes} ${period}`;
    } catch {
      return timeStr; // fallback to original if parsing fails
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<{ start: string; end: string; spotsLeft: number }[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  // We'll use this to store the selected calendar ID when fetching events
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(event.calendar_id || null);
  const [currentUser, setCurrentUser] = useState<{ id: string; email: string } | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false);
  const [customerTimezone, setCustomerTimezone] = useState<{
    timeZoneId: string;
    timeZoneName: string;
    rawOffset: number;
    dstOffset: number;
  } | null>(null);

  // Get default values from URL parameters
  const getDefaultValue = (key: string, defaultVal: string = '') => {
    return urlParams?.get(key) || defaultVal;
  };

  // Convert a time string (HH:MM) from one timezone to another
  const convertTimeBetweenTimezones = (timeStr: string, fromTimezone: string, toTimezone: string): string => {
    try {
      // Convert friendly names to IANA IDs if needed
      const fromIANA = getIANATimezone(fromTimezone);
      const toIANA = getIANATimezone(toTimezone);
      
      // Create a date object for today with the given time
      const today = new Date();
      const [hours, minutes] = timeStr.split(':').map(Number);
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
      
      // Convert to the target timezone
      const targetDate = new Date(date.toLocaleString('en-US', { timeZone: toIANA }));
      
      // Format back to HH:MM
      const targetHours = targetDate.getHours().toString().padStart(2, '0');
      const targetMinutes = targetDate.getMinutes().toString().padStart(2, '0');
      
      return `${targetHours}:${targetMinutes}`;
    } catch (error) {
      console.error('Error converting time between timezones:', error);
      return timeStr; // Return original time if conversion fails
    }
  };
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      // Set default values for address fields from initialAddress
      address: initialAddress?.address || getDefaultValue('address', ''),
      streetAddress: initialAddress?.street_address || getDefaultValue('streetAddress', ''),
      city: initialAddress?.city || getDefaultValue('city', ''),
      state: initialAddress?.state || getDefaultValue('state', ''),
      postalCode: initialAddress?.postal_code || getDefaultValue('postalCode', ''),
      country: initialAddress?.country || getDefaultValue('country', ''),
      latitude: initialAddress?.latitude || Number(getDefaultValue('latitude', '0')),
      longitude: initialAddress?.longitude || Number(getDefaultValue('longitude', '0')),
      // Form fields from URL parameters
      firstName: getDefaultValue('firstName', ''),
      lastName: getDefaultValue('lastName', ''),
      email: getDefaultValue('email', ''),
      phoneNumber: getDefaultValue('phoneNumber', ''),
      salesRepName: getDefaultValue('salesRepName', ''),
      levels: getDefaultValue('levels', '') as 'One' | 'Two' | 'Three',
      atticAccess: getDefaultValue('atticAccess', '') as 'In Garage' | 'In Bedroom' | 'In Hallway',
      mountType: getDefaultValue('mountType', '') as 'Roof Mount' | 'Ground Mount' | 'Both',
      metersOnSite: getDefaultValue('metersOnSite', '') as 'One' | 'Two' | 'Three',
      electricalPanels: getDefaultValue('electricalPanels', '') as 'One' | 'Two' | 'Three',
      animals: getDefaultValue('animals', '') as 'Dogs' | 'Cats' | 'None',
      hasBattery: getDefaultValue('hasBattery', 'No') as 'Yes' | 'No',
      requiresConstruction: getDefaultValue('requiresConstruction', 'No') as 'Yes' | 'No',
      constructionDetails: getDefaultValue('constructionDetails', ''),
      installerNote: getDefaultValue('installerNote', ''),
      selectedDate: getDefaultValue('selectedDate', ''),
      selectedTime: getDefaultValue('selectedTime', ''),
    },
  });

  const selectedDate = watch('selectedDate');
  const requiresConstruction = watch('requiresConstruction');

  // Get current logged in user
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUser({
            id: user.id,
            email: user.email || ''
          });
        }
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };

    const fetchUserData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserEmail(user.email || null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    getCurrentUser();
    fetchUserData();
  }, []);

  // Fetch user email and calendar ID for the event owner
  useEffect(() => {
    const fetchUserData = async () => {
      if (event?.user_id) {
        try {
          console.log("fetchUserData running...", event?.user_id);
          
          // Use regular supabase client if admin is not available
          const client = supabaseAdmin || supabase;
          
          if (!client) {
            console.log('No Supabase client available for user data fetching');
            return;
          }

          // Try to get user's Google Calendar connection status
          try {
            const { data: userData, error: userError } = await client
              .from('profiles')
              .select('google_calendar_connected')
              .eq('id', event.user_id)
              .single();

            if (!userError && userData) {
              setIsGoogleCalendarConnected(userData.google_calendar_connected || false);
            }
          } catch (error) {
            console.log('Could not fetch user profile data:', error);
          }

          // Try to get user email from auth
          try {
            const { data: { user }, error: authError } = await client.auth.getUser();
            
            if (!authError && user?.email) {
              setUserEmail(user.email);
            }
          } catch (error) {
            console.log('Could not fetch user auth data:', error);
          }

          // Set calendar ID from event
          setSelectedCalendarId(event.calendar_id || null);
        } catch (error) {
          console.log('Error in fetchUserData:', error);
        }
      }
    };

    fetchUserData();
  }, [event]);

  // Fetch availability data
  useEffect(() => {
    const fetchAvailability = async () => {
      console.log("fetchAvailability running...", event?.minimum_scheduling_notice);
      try {
        // Use regular supabase client if admin is not available
        const client = supabaseAdmin || supabase;
        
        if (!client) {
          console.error('No Supabase client available');
          return;
        }

        if (!event?.user_id) {
          console.error('No user_id provided in event');
          return;
        }
        
        console.log('Fetching availability for user:', event.user_id);
        const { data: availabilityData, error: availabilityError } = await client
          .from('availability')
          .select('day_of_week, start_time, end_time')
          .eq('user_id', event.user_id);

        if (availabilityError) {
          console.error('Error fetching availability:', availabilityError);
          toast.error('Error fetching availability data');
          return;
        }

        console.log('Availability data:', availabilityData);

        if (!availabilityData || availabilityData.length === 0) {
          console.log('No availability data found for user:', event.user_id);
          toast.error('No availability set for this event');
          return;
        }

        // Generate available dates from minimum_scheduling_notice to next 30 days
        const dates = [];
        const today = new Date();
        
        // Use minimum_scheduling_notice from event data (default to 2 if not set)
        const minNotice = event?.minimum_scheduling_notice || 2;
        console.log(`Using minimum scheduling notice: ${minNotice} -- ${event?.minimum_scheduling_notice}`);
        
        // Set the start date to today + minimum_scheduling_notice days
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        startDate.setDate(today.getDate() + minNotice);
        
        // Generate dates for the next 30 days from the start date
        for (let i = 0; i < 30; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          const dayOfWeek = date.getDay();
          
          // Only include dates that have availability set
          if (availabilityData.some(slot => slot.day_of_week === dayOfWeek)) {
            // Create date string in local timezone
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;
            console.log('Generated date:', {
              date: date.toString(),
              dateString,
              dayOfWeek,
              formatted: format(date, 'EEEE, MMMM d, yyyy')
            });
            dates.push(dateString);
          }
        }
        console.log('Generated available dates:', dates);
        setAvailableDates(dates);
      } catch (error) {
        console.error('Error in fetchAvailability:', error);
        toast.error('Failed to fetch availability data');
      }
    };

    // Only run fetchAvailability if we have both client and event.user_id
    if (event?.user_id) {
      fetchAvailability();
    }
  }, [event]);

  useEffect(() => {
    if (selectedDate && initialAddress) {
      getTimeSlots();
    }
  }, [selectedDate, initialAddress, calendarEvents]);

  // Fetch calendar events when date is selected
  useEffect(() => {
    const fetchCalendarEvents = async () => {
      if (!selectedDate || !isGoogleCalendarConnected) return;
      
      try {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        // Use the selectedCalendarId if available, otherwise fall back to event.calendar_id or 'primary'
        const calendarId = selectedCalendarId || event.calendar_id || 'primary';
        const userId = event.user_id || '';
        
        // Modify the listCalendarEvents call to use the specific calendar ID
        // Note: You may need to update the listCalendarEvents function in google-calendar.ts
        // to accept a calendarId parameter
        const events = await listCalendarEvents(
          userId,
          startOfDay.toISOString(),
          endOfDay.toISOString(),
          calendarId
        );
        
        // Ensure events is treated as an array or extract items property if it exists
        const eventsArray = events && Array.isArray(events) 
          ? events 
          : (typeof events === 'object' && events !== null && 'items' in events 
              ? (events as any).items 
              : []);
        
        setCalendarEvents(
          eventsArray.filter((calEvent: any) => 
            calEvent.extendedProperties?.private?.createdBy !== "solerro-site-survey"
          )
        );

        console.log('Calendar events:', eventsArray);
      } catch (error) {
        console.error('Error fetching calendar events:', error);
        // Don't show error toast as it's not critical
      }
    };
    
    if (isGoogleCalendarConnected && selectedDate) {
      fetchCalendarEvents();
    }
  }, [selectedDate, isGoogleCalendarConnected, event.user_id, event.calendar_id]);

  const getTimeSlots = async () => {
    if (!selectedDate || !event) return;

    try {
      setIsLoadingTimeSlots(true);
      
      if (!event.calendar_id) {
        console.error('No calendar ID found for this event');
        return;
      }
      
      // Use regular supabase client if admin is not available
      const client = supabaseAdmin || supabase;
      
      if (!client) {
        console.error('No Supabase client available');
        return;
      }
      
      // Get availability for the selected day
      const selectedDateObj = new Date(selectedDate);
      const dayOfWeek = selectedDateObj.getDay();

      const { data: availabilityData, error: availabilityError } = await client
        .from('availability')
        .select('start_time, end_time, timezone_id')
        .eq('user_id', event.user_id)
        .eq('day_of_week', dayOfWeek);

      if (availabilityError) {
        console.error('Error fetching availability:', availabilityError);
        toast.error('Error fetching availability data');
        return;
      }

      if (!availabilityData || availabilityData.length === 0) {
        setAvailableTimeSlots([]);
        return;
      }

      // Get existing bookings for this date and event
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      const { data: existingBookings, error: bookingsError } = await client
        .from('bookings')
        .select('start_time')
        .eq('event_id', event.id)
        .gte('start_time', startOfDay.toISOString())
        .lte('start_time', endOfDay.toISOString());

      if (bookingsError) {
        console.error('Error fetching existing bookings:', bookingsError);
        toast.error('Error fetching existing bookings');
        return;
      }

      // Count bookings per time slot
      const bookingCounts: Record<string, number> = {};
      if (existingBookings) {
        existingBookings.forEach(booking => {
          const time = format(new Date(booking.start_time), 'h:mm a');
          bookingCounts[time] = (bookingCounts[time] || 0) + 1;
        });
      }

      // Maximum bookings allowed per slot (default to 1 if not set)
      const maxBookingsPerSlot = event.max_bookings_per_slot || 1;

      // Generate time slots based on availability and event duration
      const slots = [];
      for (const slot of availabilityData) {
        // Convert availability times from provider's timezone to customer's timezone if needed
        let startTime = slot.start_time;
        let endTime = slot.end_time;
        
        if (customerTimezone && slot.timezone_id && customerTimezone.timeZoneId !== slot.timezone_id) {
          startTime = convertTimeBetweenTimezones(slot.start_time, slot.timezone_id, customerTimezone.timeZoneId);
          endTime = convertTimeBetweenTimezones(slot.end_time, slot.timezone_id, customerTimezone.timeZoneId);
        }
        
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        const currentTime = new Date(selectedDate);
        currentTime.setHours(startHour, startMinute, 0, 0);
        
        const endTimeDate = new Date(selectedDate);
        endTimeDate.setHours(endHour, endMinute, 0, 0);

        while (currentTime < endTimeDate) {
          const slotEnd = new Date(currentTime);
          slotEnd.setMinutes(slotEnd.getMinutes() + event.duration);

          if (slotEnd <= endTime) {
            const startTimeStr = format(currentTime, 'h:mm a');
            const endTimeStr = format(slotEnd, 'h:mm a');
            const bookingsCount = bookingCounts[startTimeStr] || 0;
            const spotsLeft = maxBookingsPerSlot - bookingsCount;
            
            // Check if this time slot conflicts with any Google Calendar events
            const hasCalendarConflict = calendarEvents.some(calEvent => {
              if (!calEvent.start?.dateTime || !calEvent.end?.dateTime) return false;
              
              const eventStart = parseISO(calEvent.start.dateTime);
              const eventEnd = parseISO(calEvent.end.dateTime);
              
              // Check if the slot overlaps with the calendar event
              return (
                (currentTime >= eventStart && currentTime < eventEnd) || // Slot start during event
                (slotEnd > eventStart && slotEnd <= eventEnd) || // Slot end during event
                (currentTime <= eventStart && slotEnd >= eventEnd) // Slot contains event
              );
            });
            
            // Only add slots that have spots available and don't conflict with calendar
            if (spotsLeft > 0 && !hasCalendarConflict) {
              slots.push({
                start: startTimeStr,
                end: endTimeStr,
                spotsLeft: spotsLeft
              });
            }
          }

          currentTime.setMinutes(currentTime.getMinutes() + event.duration);
        }
      }

      console.log('Generated time slots:', slots);
      setAvailableTimeSlots(slots);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      toast.error('Failed to load time slots');
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  // Call getTimeSlots when a date is selected
  useEffect(() => {
    if (selectedDate) {
      getTimeSlots();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (availableDates.length > 0) {
      console.log('Setting initial date:', availableDates[0]);
      setValue('selectedDate', availableDates[0]);
    }
  }, [availableDates, setValue]);

  // Add logging for date selection changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'selectedDate') {
        console.log('Date selected:', {
          selectedDate: value.selectedDate,
          formattedDate: value.selectedDate ? format(new Date(value.selectedDate), 'EEEE, MMMM d, yyyy') : null,
          rawValue: value
        });
      }
      if (name === 'selectedTime') {
        console.log('Time selected:', {
          selectedTime: value.selectedTime,
          selectedDate: value.selectedDate,
          fullDateTime: value.selectedDate && value.selectedTime ? 
            `${format(new Date(value.selectedDate), 'EEEE, MMMM d, yyyy')} at ${value.selectedTime}` : null
        });
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch]);

  // Fetch customer timezone when address coordinates are available
  useEffect(() => {
    const fetchCustomerTimezone = async () => {
      if (initialAddress?.latitude && initialAddress?.longitude) {
        try {
          const timezone = await getTimezoneFromCoordinates(initialAddress.latitude, initialAddress.longitude);
          setCustomerTimezone(timezone);
          console.log('Customer timezone set:', timezone);
        } catch (error) {
          console.error('Error fetching customer timezone:', error);
          // Fallback to user's timezone
          setCustomerTimezone({
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneName: 'User Timezone (fallback)',
            rawOffset: 0,
            dstOffset: 0
          });
        }
      }
    };

    fetchCustomerTimezone();
  }, [initialAddress?.latitude, initialAddress?.longitude]);

  const onFormSubmit = async (data: BookingFormData) => {
    try {
      setIsLoading(true);
      console.log('Form submission started:', {
        selectedDate: data.selectedDate,
        selectedTime: data.selectedTime,
        rawFormData: data
      });

      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Use stored customer timezone or fallback to user's timezone
        const timezoneToUse = customerTimezone || {
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneName: 'User Timezone (fallback)',
          rawOffset: 0,
          dstOffset: 0
        };
        
        console.log('Using timezone for booking:', timezoneToUse);

        // Parse the date and time
        const [year, month, day] = data.selectedDate.split('-').map(Number);
        console.log('Parsed date components:', { year, month, day });
        
        // Parse the time from the selected time slot (format: "h:mm a")
        const timeMatch = data.selectedTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!timeMatch) {
          throw new Error('Invalid time format');
        }
        
        const [_, hoursStr, minutesStr, period] = timeMatch;
        let hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);
        
        console.log('Parsed time components:', { 
          hoursStr, 
          minutesStr, 
          period,
          hours,
          minutes
        });
        
        // Convert to 24-hour format
        if (period.toUpperCase() === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
          hours = 0;
        }

        console.log('Converted to 24-hour format:', { hours, minutes });

        // Create the date in the customer's timezone (project address timezone)
        // This ensures the booking is at the correct time in the customer's location
        const customerDate = new Date(year, month - 1, day, hours, minutes);
        console.log('Created customer date:', {
          customerDate: customerDate.toString(),
          customerDateISO: customerDate.toISOString(),
          customerDateComponents: {
            year: customerDate.getFullYear(),
            month: customerDate.getMonth() + 1,
            day: customerDate.getDate(),
            hours: customerDate.getHours(),
            minutes: customerDate.getMinutes()
          }
        });
        
        // Convert the customer's local time to UTC for storage
        // This is the correct approach: customer's local time -> UTC
        const startTime = convertLocalTimeToUTC(customerDate, timezoneToUse.timeZoneId);
        
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + event.duration);

        console.log('Time conversion details:', {
          customerDate: customerDate.toString(),
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          selectedDate: data.selectedDate,
          selectedTime: data.selectedTime,
          customerTimezone: timezoneToUse.timeZoneId,
          customerTimezoneName: timezoneToUse.timeZoneName,
          customerStartTimeLocal: customerDate.toString(),
          customerEndTimeLocal: new Date(customerDate.getTime() + event.duration * 60000).toString(),
          payload: {
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString()
          }
        });

        // Get timezone info for customer's location
        const ianaZone = timezoneToUse.timeZoneId;
        const friendlyZone = timeZoneMap[ianaZone] || ianaZone;

        const extraInformation = {
          levels: data.levels,
          atticAccess: data.atticAccess,
          mountType: data.mountType,
          metersOnSite: data.metersOnSite,
          electricalPanels: data.electricalPanels,
          animals: data.animals,
          hasBattery: data.hasBattery,
          requiresConstruction: data.requiresConstruction,
          constructionDetails: data.constructionDetails,
          installerNote: data.installerNote,
          address: {
            full: data.address,
            street: data.streetAddress,
            city: data.city,
            state: data.state,
            postalCode: data.postalCode,
            country: data.country,
            coordinates: {
              latitude: data.latitude,
              longitude: data.longitude,
            },
          },
          // Add user email and calendar ID for the trigger
          userEmail: userEmail,
          calendarId: event.calendar_id,
          // Add current user information
          currentUser: currentUser ? {
            id: currentUser.id,
            email: currentUser.email
          } : null,
          // Add formatted date and time information for the webhook using customer's timezone
          webhookData: {
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            local_start_time: customerDate.toISOString(),
            local_end_time: new Date(customerDate.getTime() + event.duration * 60000).toISOString(),
            timezone: ianaZone,
            timezone_friendly: friendlyZone,
            formatted_date: format(customerDate, 'MMMM d, yyyy'),
            formatted_time: format(customerDate, 'h:mm a'),
            formatted_datetime: format(customerDate, 'MMMM d, yyyy h:mm a')
          }
        };

        console.log('Submitting booking with data:', {
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          event_id: event.id,
          project_id: projectId,
          extra_information: extraInformation
        });

        // Fetch the project's tenant_id
        let tenantId = null;
        if (projectId) {
          const { data: projectData, error: projectError } = await supabase
            .from('projects')
            .select('tenant_id')
            .eq('id', projectId)
            .single();
          
          if (projectError) {
            console.error('Error fetching project tenant_id:', projectError);
            throw new Error('Failed to fetch project information');
          }
          
          tenantId = projectData?.tenant_id;
        }

        const { data: bookingResult, error: bookingError } = await supabase
          .from('bookings')
          .insert({
            event_id: event.id,
            project_id: projectId,
            tenant_id: tenantId,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phoneNumber,
            sales_rep_name: data.salesRepName,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            extra_information: extraInformation,
          })
          .select()
          .single();

        if (bookingError) throw bookingError;

        // Send booking notifications
        if (bookingResult) {
          try {
            await bookingNotificationService.notifyBookingCreated({
              bookingId: bookingResult.id,
              projectId: projectId,
              customerName: `${data.firstName} ${data.lastName}`,
              customerEmail: data.email,
              salesRepName: data.salesRepName,
              bookingDate: data.selectedDate,
              bookingTime: data.selectedTime,
              address: data.address,
              eventName: event.name,
              eventType: event.meeting_type || 'Site Survey',
              customerTimezone: timezoneToUse.timeZoneId,
              metadata: {
                phoneNumber: data.phoneNumber
              }
            });
          } catch (notificationError) {
            console.error('Error sending booking notifications:', notificationError);
            // Don't fail the booking if notifications fail
          }
        }

        toast.success('Booking submitted successfully');

        // Get sales rep email if project exists
        let salesRepEmail = null;
        if (projectId) {
          try {
            const { data: project } = await supabase
              .from('projects')
              .select(`
                sales_rep:users!assigned_sales_rep_id (
                  email,
                  first_name,
                  last_name
                )
              `)
              .eq('id', projectId)
              .single() as { data: { sales_rep: { email: string; first_name: string; last_name: string } | null } | null; error: any };

            salesRepEmail = project?.sales_rep?.email;
          } catch (error) {
            console.warn('Could not fetch sales rep email for confirmation message:', error);
          }
        }

        // Navigate to confirmation page with booking details
        console.log('Navigating to booking confirmation with details:', {
          customerName: `${data.firstName} ${data.lastName}`,
          customerEmail: data.email,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          customerStartTime: customerDate.toString(),
          customerEndTime: new Date(customerDate.getTime() + event.duration * 60000).toString(),
          salesRepEmail: salesRepEmail
        });

        navigate('/app/booking-confirmation', {
          state: {
            bookingDetails: {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phoneNumber: data.phoneNumber,
              startTime: startTime.toISOString(),
              endTime: endTime.toISOString(),
              eventName: event.name,
              meetingType: event.meeting_type,
              meetingLink: event.meeting_link,
              address: data.address,
              extraInformation: extraInformation,
              // Add customer timezone information
              customerTimezone: timezoneToUse.timeZoneId,
              customerTimezoneName: timezoneToUse.timeZoneName,
              customerStartTime: customerDate.toString(),
              customerEndTime: new Date(customerDate.getTime() + event.duration * 60000).toString(),
              // Add sales rep info for confirmation message
              salesRepEmail: salesRepEmail,
              confirmationEmailSentTo: salesRepEmail || data.email
            },
          },
        });
      }
    } catch (error) {
      console.error('Booking submission failed:', error);
      toast.error('Failed to submit booking');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
      {/* Display the installation address at the top of the form */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-foreground mb-2">Installation Address</h3>
        <p className="text-foreground font-medium">{watch('address')}</p>
        
        {/* Hidden address fields to maintain form state */}
        <input type="hidden" {...register('streetAddress')} />
        <input type="hidden" {...register('city')} />
        <input type="hidden" {...register('state')} />
        <input type="hidden" {...register('postalCode')} />
        <input type="hidden" {...register('country')} />
        <input type="hidden" {...register('latitude')} />
        <input type="hidden" {...register('longitude')} />
      </div>

      {/* Schedule Section - Moved up to match Ensite form */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Schedule</h3>
        <div className="space-y-6">
          <div>
            <label htmlFor="selectedDate" className="block text-sm font-medium text-foreground">
              Select Date
            </label>
            <select
              id="selectedDate"
              {...register('selectedDate')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            >
              {availableDates.map((date) => {
                // Display dates in the customer's timezone (project address timezone)
                // This ensures the agent sees the correct date for the customer's location
                const parsedDate = new Date(date);
                const customerTimezoneId = customerTimezone?.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                
                // Format the date in the customer's timezone
                const customerDate = new Date(parsedDate.toLocaleString('en-US', { timeZone: customerTimezoneId }));
                const formattedDate = format(customerDate, 'EEEE, MMMM d, yyyy');
                
                console.log('Rendering date option:', {
                  originalDate: date,
                  parsedDate: parsedDate.toString(),
                  customerDate: customerDate.toString(),
                  customerTimezone: customerTimezoneId,
                  formattedDate: formattedDate,
                  // Store the original date as the value to avoid timezone conversion issues
                  value: date
                });
                
                return (
                  <option key={date} value={date}>
                    {formattedDate}
                  </option>
                );
              })}
            </select>
            {errors.selectedDate && (
              <p className="mt-1 text-sm text-destructive">{errors.selectedDate.message}</p>
            )}
          </div>

          {selectedDate && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Time
              </label>
              {isLoadingTimeSlots ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-sm text-muted-foreground">Loading available time slots...</p>
                </div>
              ) : availableTimeSlots.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {availableTimeSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setValue('selectedTime', slot.start)}
                      className={`w-full py-3 rounded-xl border transition-colors text-base font-medium flex items-center justify-center
                        ${watch('selectedTime') === slot.start
                          ? 'bg-primary hover:bg-primaryHover text-white'
                          : 'bg-white text-foreground border border-border hover:bg-gray-50'
                        }
                        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                    >
                      {formatTimeSlot(slot.start)} - {formatTimeSlot(slot.end)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No available time slots for this date.</p>
              )}
              {errors.selectedTime && (
                <p className="mt-1 text-sm text-destructive">{errors.selectedTime.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Contact Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              {...register('firstName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-destructive">{errors.firstName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              {...register('lastName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-destructive">{errors.lastName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Email
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-foreground">
              Phone Number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              {...register('phoneNumber')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-destructive">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="salesRepName" className="block text-sm font-medium text-foreground">
              Sales Representative Name
            </label>
            <input
              type="text"
              id="salesRepName"
              {...register('salesRepName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.salesRepName && (
              <p className="mt-1 text-sm text-destructive">{errors.salesRepName.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Property Details Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Property Details</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Number of Levels
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="levels-one"
                  type="radio"
                  value="One"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="levels-two"
                  type="radio"
                  value="Two"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="levels-three"
                  type="radio"
                  value="Three"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.levels && (
              <p className="mt-1 text-sm text-destructive">{errors.levels.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Attic Access Location
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="attic-garage"
                  type="radio"
                  value="In Garage"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-garage" className="ml-3 block text-sm font-medium text-foreground">
                  In Garage
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="attic-bedroom"
                  type="radio"
                  value="In Bedroom"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-bedroom" className="ml-3 block text-sm font-medium text-foreground">
                  In Bedroom
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="attic-hallway"
                  type="radio"
                  value="In Hallway"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-hallway" className="ml-3 block text-sm font-medium text-foreground">
                  In Hallway
                </label>
              </div>
            </div>
            {errors.atticAccess && (
              <p className="mt-1 text-sm text-destructive">{errors.atticAccess.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type of Mount
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="mount-roof"
                  type="radio"
                  value="Roof Mount"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-roof" className="ml-3 block text-sm font-medium text-foreground">
                  Roof Mount
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="mount-ground"
                  type="radio"
                  value="Ground Mount"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-ground" className="ml-3 block text-sm font-medium text-foreground">
                  Ground Mount
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="mount-both"
                  type="radio"
                  value="Both"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-both" className="ml-3 block text-sm font-medium text-foreground">
                  Both
                </label>
              </div>
            </div>
            {errors.mountType && (
              <p className="mt-1 text-sm text-destructive">{errors.mountType.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Meters on Site
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="meters-one"
                  type="radio"
                  value="One"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="meters-two"
                  type="radio"
                  value="Two"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="meters-three"
                  type="radio"
                  value="Three"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.metersOnSite && (
              <p className="mt-1 text-sm text-destructive">{errors.metersOnSite.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Electrical Panels on Site
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="panels-one"
                  type="radio"
                  value="One"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="panels-two"
                  type="radio"
                  value="Two"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="panels-three"
                  type="radio"
                  value="Three"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.electricalPanels && (
              <p className="mt-1 text-sm text-destructive">{errors.electricalPanels.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Animals Present
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="animals-dogs"
                  type="radio"
                  value="Dogs"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-dogs" className="ml-3 block text-sm font-medium text-foreground">
                  Dogs
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="animals-cats"
                  type="radio"
                  value="Cats"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-cats" className="ml-3 block text-sm font-medium text-foreground">
                  Cats
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="animals-none"
                  type="radio"
                  value="None"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-none" className="ml-3 block text-sm font-medium text-foreground">
                  None
                </label>
              </div>
            </div>
            {errors.animals && (
              <p className="mt-1 text-sm text-destructive">{errors.animals.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Does this project have a battery?
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="battery-yes"
                  type="radio"
                  value="Yes"
                  {...register('hasBattery')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="battery-yes" className="ml-3 block text-sm font-medium text-foreground">
                  Yes
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="battery-no"
                  type="radio"
                  value="No"
                  {...register('hasBattery')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="battery-no" className="ml-3 block text-sm font-medium text-foreground">
                  No
                </label>
              </div>
            </div>
            {errors.hasBattery && (
              <p className="mt-1 text-sm text-destructive">{errors.hasBattery.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Construction Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Construction</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Does this project require any construction or electrical work?
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="construction-yes"
                  type="radio"
                  value="Yes"
                  {...register('requiresConstruction')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="construction-yes" className="ml-3 block text-sm font-medium text-foreground">
                  Yes
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="construction-no"
                  type="radio"
                  value="No"
                  {...register('requiresConstruction')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="construction-no" className="ml-3 block text-sm font-medium text-foreground">
                  No
                </label>
              </div>
            </div>
            {errors.requiresConstruction && (
              <p className="mt-1 text-sm text-destructive">{errors.requiresConstruction.message}</p>
            )}
          </div>

          {requiresConstruction === 'Yes' && (
            <div className="sm:col-span-2">
              <label htmlFor="constructionDetails" className="block text-sm font-medium text-foreground">
                Please explain the construction or electrical work needed
              </label>
              <textarea
                id="constructionDetails"
                rows={3}
                {...register('constructionDetails')}
                className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
                placeholder="Describe the construction or electrical work needed..."
              />
              {errors.constructionDetails && (
                <p className="mt-1 text-sm text-destructive">{errors.constructionDetails.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Installer Note Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Installer Note</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="installerNote" className="block text-sm font-medium text-foreground">
              Note to Installer (From Sales Rep)
            </label>
            <textarea
              id="installerNote"
              rows={4}
              {...register('installerNote')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
              placeholder="Add any special instructions or notes for the installer..."
            />
            {errors.installerNote && (
              <p className="mt-1 text-sm text-destructive">{errors.installerNote.message}</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
        >
          {isLoading ? 'Submitting...' : 'Book Appointment'}
        </button>
      </div>
    </form>
  );
}